<template>
  <div class="slice mask-arc-b md:mask-wave-b bg-white" :data-widget-title="content.heroTitle">
    <AppPromptAboveHero v-if="showAppPrompt" :prompt-type="promptTypeAbove" />
    <div
      class="relative px-box-2 pt-11 md:pt-28"
      :class="[
        { 'page-hero before:h-5/6 sm:px-box-4': !showAppPrompt },
        { 'md:page-hero before:h-4/5 pt-12': showAppPrompt },
      ]"
    >
      <h1
        id="hero-search-form"
        class="relative title text-24 md:tagline md:text-38 md:text-white lg:text-center mx-auto sm:w-136 md:w-152 lg:w-184"
        :class="{ 'tagline text-white': !showAppPrompt, 'xl:after:hidden': showExperimentalLayout }"
      >
        {{ content.heroTitle }}
      </h1>
      <div :class="{ 'md:hidden': !showExperimentalLayout }">
        <MobileSearchForm
          class="box-container overflow-visible mt-8 md:mt-24 xl:mt-8 py-7 md:py-6 xl:py-4 px-4 md:mx-auto md:w-168 lg:w-184 md:grid md:gap-x-8 md:grid-cols-2 xl:flex xl:w-auto max-w-6xl"
          :default-country-from="page?.context.countryFrom ?? undefined"
          :default-country-to="page?.context.countryTo ?? undefined"
          :default-currency-from="String(route.params.currencyFrom)"
          :default-currency-to="String(route.params.currencyTo)"
          :default-amount="defaultAmount"
          :payout="page?.context.payout ?? undefined"
        />
      </div>
      <div v-if="!showExperimentalLayout" class="hidden md:block">
        <TransferSearchForm
          class="box-container overflow-visible mt-8 md:mt-24 sm:mx-20 md:mx-auto md:w-168 lg:w-184"
          :default-country-from="page?.context.countryFrom ?? undefined"
          :default-country-to="page?.context.countryTo ?? undefined"
          :default-currency-from="String(route.params.currencyFrom)"
          :default-currency-to="String(route.params.currencyTo)"
          :default-amount="defaultAmount"
          :payout="page?.context.payout ?? undefined"
        />
      </div>
    </div>
    <AppPromptBelowHero v-if="showAppPrompt" :prompt-type="promptType" />
    <HeroTrustSection
      :class="{ 'lg:hidden': showAppPrompt }"
      :blocks="content.featuredBlocks"
      :featured-on="content.featuredOn"
      :hide-provider-logos-mobile="showAppPrompt"
    />
  </div>
</template>

<script lang="ts" setup>
import type { GQLHeroSearchFormSlice } from './generated/hero-search-form'
import { PageTypes } from '@core/constants/pages'
import type { GQLPageMeta } from '@core/composables/use-page-meta/generated/page-meta'
import HeroTrustSection from './hero-trust-section.vue'

defineProps<{ content: GQLHeroSearchFormSlice }>()
const page = inject<GQLPageMeta>('pageContext')

const { $features } = useNuxtApp()

const route = useRoute()

const showExperimentalLayout = computed(() => {
  return !__IS_MOBILE_APP__ && $features?.getValue('new_desktop_imt_search_form') === 'on'
})

const defaultAmount = computed(() => {
  return Number(route.params.amount) || Number(page?.context.amount) || 0
})

const promptType = computed(() => {
  switch (route.meta.pageType) {
    case PageTypes.TRANSFER:
      return 'HeroSearchForm_IMT'
    case PageTypes.COUNTRY:
      return 'HeroSearchForm_Country'
    case PageTypes.HOME:
    default:
      return 'HeroSearchForm_Home'
  }
})

const promptTypeAbove = computed<
  'AboveHeroSearchForm_Country' | 'AboveHeroSearchForm_Home' | 'AboveHeroSearchForm_IMT'
>(() => `Above${promptType.value}`)

const showAppPrompt = computed(() => {
  if (__IS_MOBILE_APP__) {
    return false
  }
  return (
    route.meta.pageType === PageTypes.HOME ||
    route.meta.pageType === PageTypes.TRANSFER ||
    route.meta.pageType === PageTypes.COUNTRY
  )
})
</script>

<style scoped>
@screen md {
  .slice {
    @apply bg-no-repeat;
    background-size: 240px;
    background-position-y: bottom;
    background-image: url('@core/images/decoration.svg');
  }
}
</style>
